import './SortByFilter.scss';

import { getDirectParent } from '@lib/popups';
import { MenuInfo } from 'rc-menu/es/interface';
import { SanitizedHtml } from '@ui/general';
import { UpdateQueryParams } from '@hooks';
import { When } from 'react-if';

import { DownOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { Dropdown, Tooltip } from 'antd';
import React, { memo, useCallback } from 'react';
import { SonarSortDirection, SonarV3SortDirection, SortByOption } from '@sonar-software/sonar-ts-types';

export type SortByFilterQueryParams = {
	sortBy?: string;
	sortDirection?: SonarSortDirection;
};

export interface SortByFilterProps {
	defaultSortOption: SortByOption;
	onChange: UpdateQueryParams<SortByFilterQueryParams>;
	queryParams: SortByFilterQueryParams;
	options: SortByOption[];
	sortIcon?: boolean;
}

export const SortByFilter: React.FC<SortByFilterProps> = memo(
	({ defaultSortOption, onChange, queryParams, options, sortIcon }) => {
		const sortBy = queryParams.sortBy ?? defaultSortOption.key;
		const sortDir = queryParams.sortDirection ?? defaultSortOption.direction;
		const currentSortBy = options.find((option) => option.key === sortBy)?.label;

		const sortText = `Sorted by: ${currentSortBy}`;

		const updateFilter = useCallback(
			(sort: SortByOption) => {
				if (sort.key !== sortBy || sort.direction !== sortDir) {
					onChange({
						sortBy: sort.key,
						sortDirection: sort.direction
					});
				}
			},
			[ onChange, sortBy, sortDir ]
		);

		const handleMenuClick = useCallback(
			(e: MenuInfo) => {
				const targetOption = options.find((option) => option.key === e.key) ?? options[ 0 ];
				updateFilter(targetOption);
			},
			[ options, updateFilter ]
		);

		const sortOutlined =
			sortDir === SonarV3SortDirection.Ascending ?
				<SortAscendingOutlined
					onClick={ updateFilter.bind(this, {
						direction: SonarV3SortDirection.Descending,
						key: sortBy
					}) } />			 :
				<SortDescendingOutlined
					onClick={ updateFilter.bind(this, {
						direction: SonarV3SortDirection.Ascending,
						key: sortBy
					}) } />;

		return (
			<div className='sort-by-filter'>
				<Dropdown
					disabled={ options.length <= 1 }
					getPopupContainer={ getDirectParent }
					menu={ {
						items: options.map(({ disabled, disabledTooltip, key, label }) => ({
							className: 'sort-by-filter-entry',
							disabled,
							key,
							label: <Tooltip
								open={ disabled && disabledTooltip ? undefined : false }
								title={ <SanitizedHtml
									as='span'
									html={ disabledTooltip ?? '' } /> }>
								{ label }
							</Tooltip>
						})),
						onClick: handleMenuClick
					} }>
					<div className='periscope-sort-by-text'>
						{ sortText }
						{ options.length > 1 && <DownOutlined /> }
					</div>
				</Dropdown>
				<When condition={ sortIcon }>
					<div className='sort-icon'>{ sortOutlined }</div>
				</When>
			</div>
		);
	}
);
